import React, { useCallback } from 'react';
import { cx } from 'linaria';
import * as RadixDialog from '@radix-ui/react-dialog';

import { useTheme, Typography, noop } from '../';
import { makeTestId } from '../utils/common';
import { IconCancel } from '../foundation/icons';
import * as styles from './dialog.styles';

export interface DialogProps {
    /**
     *  Title of the dialog
     */
    header: string;
    /**
     *  Width of the dialog container
     *  @default medium
     */
    size?: 'small' | 'medium' | 'large';
    /**
     *  Test id for unit and E2E tests
     */
    testId?: string;
    /**
     *  Controlled open state for the dialog
     */
    open: boolean;
    /**
     * Called when open state changes
     */
    onChange?: (open: boolean) => void;
    /**
     * prop to define if the X button is visible or not
     */
    hideCloseButton?: boolean;
    /**
     * prop to define if clicking outside the modal/pressing (space/escape/enter) should close the dialog or not
     */
    allowDialogClosure?: boolean;
}

const DialogContext = React.createContext<{
    header: DialogProps['header'];
    size: DialogProps['size'];
    testId?: DialogProps['testId'];
    hideCloseButton?: DialogProps['hideCloseButton'];
    allowDialogClosure?: DialogProps['allowDialogClosure'];
}>({ size: 'medium', header: '', testId: null, hideCloseButton: false, allowDialogClosure: true });

export const Dialog = (props: React.PropsWithChildren<DialogProps>) => {
    const {
        children,
        header,
        size = 'medium',
        open = false,
        onChange,
        testId,
        hideCloseButton,
        allowDialogClosure,
    } = props;

    const value = React.useMemo(() => {
        return {
            header,
            size,
            testId,
            hideCloseButton,
            allowDialogClosure,
        };
    }, [header, size, testId, hideCloseButton, allowDialogClosure]);

    return (
        <div>
            <RadixDialog.Root open={open} onOpenChange={onChange}>
                <DialogContext.Provider value={value}>{children}</DialogContext.Provider>
            </RadixDialog.Root>
        </div>
    );
};

function DialogTrigger(props: React.PropsWithChildren<any>) {
    const { children } = props;

    return <RadixDialog.Trigger asChild>{children}</RadixDialog.Trigger>;
}

function DialogContent(props: React.PropsWithChildren<{ testId?: string }>) {
    const { children, testId: propTestId } = props;

    const theme = useTheme();
    const dialogValue = React.useContext(DialogContext);

    const testId = propTestId ?? dialogValue.testId;
    const allowDialogClosure = dialogValue.allowDialogClosure ?? true;

    const onKeyDownHandler = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            const key = event.key;
            const keyCodeList = [' ', 'Escape', 'Enter'];

            if (keyCodeList.includes(key)) {
                if (!allowDialogClosure) {
                    event.preventDefault();
                }
            }
        },
        [allowDialogClosure],
    );

    const onInteractOutsideHandler = useCallback(
        (event) => {
            if (!allowDialogClosure) {
                event.preventDefault();
            }
        },
        [allowDialogClosure],
    );

    return (
        <RadixDialog.Portal>
            <div data-theme={theme} data-testid={testId} className={styles.root}>
                <RadixDialog.Overlay asChild>
                    <div className={cx(styles.overlay)} data-testid={makeTestId(testId, 'overlay')} />
                </RadixDialog.Overlay>
                <RadixDialog.Content
                    asChild
                    onKeyDown={allowDialogClosure ? noop : (e) => onKeyDownHandler(e)}
                    onInteractOutside={allowDialogClosure ? noop : (e) => onInteractOutsideHandler(e)}
                >
                    <div className={cx(styles.container, styles.sizes[dialogValue.size])}>
                        <div className={styles.header}>
                            <RadixDialog.Title asChild>
                                <Typography variant="heading-3" testId={makeTestId(testId, 'title')}>
                                    {dialogValue.header}
                                </Typography>
                            </RadixDialog.Title>
                            {!dialogValue.hideCloseButton ? (
                                <RadixDialog.Close asChild>
                                    <button
                                        className={styles.closeButton}
                                        aria-label="Close dialog"
                                        data-testid={makeTestId(testId, 'close')}
                                    >
                                        <IconCancel height={16} width={16} fill="currentColor" />
                                    </button>
                                </RadixDialog.Close>
                            ) : null}
                        </div>
                        {children}
                    </div>
                </RadixDialog.Content>
            </div>
        </RadixDialog.Portal>
    );
}

function DialogFooter(props: React.PropsWithChildren<{ testId?: string }>) {
    const { children, testId } = props;

    return (
        <div data-tesid={testId} className={styles.footer}>
            {children}
        </div>
    );
}

Dialog.displayName = 'Dialog';

Dialog.Trigger = DialogTrigger;
Dialog.Content = DialogContent;
Dialog.Footer = DialogFooter;
